import { useTheme } from '@mui/material/styles';
import type {
  AgBarSeriesOptions,
  AgCartesianSeriesOptions
} from 'ag-charts-enterprise';
import { type ReactElement, useMemo } from 'react';
import ContentNotification from '@/components/ContentNotfication';
import RadialGaugeChart from '@/components/charts/RadialGaugeChart';
import VerticalBarChart from '@/components/charts/VerticalBarChart';
import GenericChartWidget from '@/components/GenericChartWidget';
import { LOG_PRODUCTS, PRODUCTS } from '@/constants/products.constants';
import type { ProductDTO } from '@/pages/Organizations';

import { useFetchUsage, useFetchUsageLimits } from '../Dashboard.hooks';
import { extractLimitMessage, formatUsageGaugeText } from '../Dashboard.utils';
import useDashboard from '../useDashboard.hooks';
import useStyles from './DashboardUsage.styles';

const DashboardUsage = () => {
  const {
    logUsageLimit,
    logUsageByDate,
    networkUsageByDate,
    networkUsageLimit,
    dashboardTimeFrame,
    usageAverages,
    orgProducts,
    selectedOrganization
  } = useDashboard();
  const theme = useTheme();
  const { isFetching: isFetchingLimits } = useFetchUsageLimits();
  const { isFetching: isFetchingUsage } = useFetchUsage(orgProducts);

  const hasNetwork =
    orgProducts.findIndex(
      (f: ProductDTO) => f.product_type === PRODUCTS.NET
    ) !== -1;
  const hasLog =
    orgProducts.findIndex((f: ProductDTO) =>
      LOG_PRODUCTS.includes(f.product_type)
    ) !== -1;

  const { classes } = useStyles({ hasLog, hasNetwork });

  const usageBarLineSeries: AgCartesianSeriesOptions[] = [
    {
      type: 'bar',
      xKey: 'date',
      yKey: 'usage',
      yName: 'Usage',
      itemStyler: (params: { datum: { usage: number; limit: number } }) => {
        let fill = theme.palette.info.main;
        if (params.datum.usage > params.datum.limit) {
          fill = theme.palette.warning.main;
        }
        return { fill };
      }
    },
    {
      type: 'line',
      xKey: 'date',
      yKey: 'limit',
      yName: 'License Limit',
      stroke: theme.palette.error.main,
      lineDash: [8, 8],
      marker: {
        enabled: false
      }
    }
  ];

  const logIngestion = logUsageByDate.map((item) => {
    return { ...item, limit: logUsageLimit };
  });

  const networkUtilization = networkUsageByDate.map((item) => {
    return { ...item, limit: networkUsageLimit };
  });

  const logIngestionPercent = logUsageLimit
    ? (usageAverages.log / logUsageLimit) * 100
    : 0;
  const networkUtilizationPercent = networkUsageLimit
    ? (usageAverages.network / networkUsageLimit) * 100
    : 0;

  const logIngestionPercentDescription = useMemo(() => {
    return formatUsageGaugeText(
      dashboardTimeFrame,
      usageAverages.log,
      logIngestionPercent,
      'log',
      logUsageLimit
    );
  }, [
    dashboardTimeFrame,
    logIngestionPercent,
    logUsageLimit,
    usageAverages.log
  ]);

  const networkUtilizationPercentDescription = useMemo(() => {
    return formatUsageGaugeText(
      dashboardTimeFrame,
      usageAverages.network,
      networkUtilizationPercent,
      'network',
      networkUsageLimit
    );
  }, [
    dashboardTimeFrame,
    networkUsageLimit,
    networkUtilizationPercent,
    usageAverages.network
  ]);

  const logLimitMessage = logIngestionPercentDescription
    ? extractLimitMessage(logIngestionPercentDescription as ReactElement, 'Log')
    : undefined;
  const networkLimitMessage = networkUtilizationPercentDescription
    ? extractLimitMessage(
        networkUtilizationPercentDescription as ReactElement,
        'Network'
      )
    : undefined;

  return (
    <>
      {selectedOrganization.label === 'all' && (
        <ContentNotification
          title="Select an organization"
          text="Please select an organization to view its usage information."
        />
      )}
      {selectedOrganization.label !== 'all' && (
        <div className={classes.dashboardUsagePage}>
          {hasLog && (
            <div className={classes.dashboardThirdWidthItem}>
              <GenericChartWidget
                title="Log Ingestion"
                loading={isFetchingUsage || isFetchingLimits}
              >
                <RadialGaugeChart
                  description={logIngestionPercentDescription}
                  limit={logUsageLimit || 0}
                  limitMessage={logLimitMessage}
                  percent={parseFloat(usageAverages.log.toFixed(2))}
                  primaryLabel={usageAverages.log.toFixed(2)}
                  secondaryLabel={
                    usageAverages.log >= 0 ? 'GB/day' : '0 GB/day'
                  }
                  title={`Trailing ${dashboardTimeFrame}-day average`}
                />
              </GenericChartWidget>
            </div>
          )}
          {hasLog && (
            <div className={classes.dashboardUsageBarGraph}>
              <GenericChartWidget
                title={`Log Ingestion Over ${dashboardTimeFrame} Days (GB/Day)`}
                loading={isFetchingLimits || isFetchingUsage}
              >
                <VerticalBarChart
                  data={logIngestion}
                  series={usageBarLineSeries as AgBarSeriesOptions[]}
                  suppressExponentialAxis
                  suppressDoubleAxis
                  suppressLegend
                />
              </GenericChartWidget>
            </div>
          )}
          {hasNetwork && (
            <div className={classes.dashboardThirdWidthItem}>
              <GenericChartWidget
                title="Network Bandwidth Usage"
                loading={isFetchingUsage || isFetchingLimits}
              >
                <RadialGaugeChart
                  description={networkUtilizationPercentDescription}
                  limit={networkUsageLimit || 0}
                  limitMessage={networkLimitMessage}
                  percent={parseFloat(usageAverages.network.toFixed(2))}
                  primaryLabel={usageAverages.network.toFixed(2)}
                  secondaryLabel={
                    usageAverages.network >= 0 ? 'Mbps' : '0 Mbps'
                  }
                  title={`Trailing ${dashboardTimeFrame}-day average`}
                />
              </GenericChartWidget>
            </div>
          )}
          {hasNetwork && (
            <div className={classes.dashboardUsageBarGraph}>
              <GenericChartWidget
                title={`Network Bandwidth Utilization Over ${dashboardTimeFrame} Days (Mbps)`}
                loading={isFetchingLimits || isFetchingUsage}
              >
                <VerticalBarChart
                  data={networkUtilization}
                  series={usageBarLineSeries as AgBarSeriesOptions[]}
                  suppressExponentialAxis
                  suppressDoubleAxis
                  suppressLegend
                />
              </GenericChartWidget>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default DashboardUsage;
